import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import { PunctualCourseDashboard } from "../../../Objects/models/PunctualCourseDashboard";
import { PunctualCoursePassed } from "../../../Objects/models/PunctualCourseDashboardUpdate";
import PunctualCourseRepository from "../../../Repositories/PunctualCourseRepository";
import { ENV_DECLARONS, isBrandActive } from "../../../config/environment.config";
import { normalizeSessionDescriptionOverride } from "../../../Utils/sessionDescriptionOverride";
import { sessionsToApiPayload } from "../../DeclarationCourse/SessionList";
import { DeclarationError, submitBuiltCourse } from "../../DeclarationV2/api/submitDeclaration";
import { Draft } from "../types";
import { draftHours, draftPeriod } from "../drafts";

/**
 * Déclaration d'un brouillon : copie de `coursePayload` et de la chaîne
 * `handleSubmitCourse` de `CourseRowActions.tsx` (brouillon :
 * `isCoursePass = true`), sans effets Redux :
 * 1. le cours est créé pour le mois en cours avec `paymentMean: 'DRAFT'`,
 *    ses séances décalées, le prix (`prix horaire payé × 2 × (1 − frais)`),
 *    l'acompte certifié de nouveau et le déroulement propre au cours ;
 * 2. proposition puis acceptation, comme toute déclaration ;
 * 3. le cours source du mois précédent est marqué « repris »
 *    (`course_passed`) pour sortir de la liste des brouillons.
 */
export const buildDraftCourse = (draft: Draft, teachr: Teachr, genericDescription: string | null): PunctualCourseDashboard => {
    const { start, end } = draftPeriod(draft);
    const hours = draftHours(draft);
    const fees = parseFloat((teachr as any)?.defaultFees ?? "0") || 0;
    const price = parseFloat(draft.price) || 0;
    const course = new PunctualCourseDashboard(
        draft.student.childId,
        "",
        draft.raw?.address_id,
        draft.raw?.subjectAsked_id,
        start ? start.toDate() : "",
        end ? end.toDate() : "",
        false,
        teachr?.id,
        false,
        (hours * 2).toString(),
        true,
        false,
        "DRAFT",
        true,
        "",
        "",
        draft.multiple
    );
    course.isPassedCourse = false;
    course.brand = isBrandActive(ENV_DECLARONS) ? "declarons" : undefined;
    course.sessions = draft.multiple ? sessionsToApiPayload(draft.sessions) : null;
    // relation Teach'r : le tarif fixé est envoyé tel quel (l'API le reprend de custom_fees)
    course.teachrPrice = (draft.isRelationTeachr ? price : price * 2 * (1 - fees)).toString() as any;
    course.sessionDescriptionOverride = normalizeSessionDescriptionOverride(draft.sessionDescriptionOverride, genericDescription);
    const deposit = draft.deposit;
    if (deposit.enabled && parseFloat(deposit.amount) > 0) {
        course.deposit = parseFloat(deposit.amount);
        course.depositDate = deposit.date ? moment(deposit.date).toDate() : new Date();
        // brouillon re-déclaré : nouvelle attestation d'acompte pour le nouveau cours
        course.depositAttestationAccepted = deposit.attestationAccepted === true;
        if (deposit.proof?.id) {
            if (draft.multiple) course.proofDeposits = [deposit.proof.id];
            else course.proofDeposit = deposit.proof.id;
        }
    }
    return course;
};

export const submitDraft = async (draft: Draft, teachr: Teachr, genericDescription: string | null): Promise<{ courseId: number }> => {
    const result = await submitBuiltCourse(buildDraftCourse(draft, teachr, genericDescription), draft.student.clientId, teachr);
    // Le cours source quitte la liste des brouillons. Un échec ici n'annule pas
    // la déclaration déjà acceptée : l'ancien parcours fait de même.
    try {
        await PunctualCourseRepository.setCoursePassed(draft.id, new PunctualCoursePassed(true));
    } catch (e) {
        console.warn("Marquage du brouillon comme repris échoué", e);
    }
    return result;
};

export { DeclarationError };
export default submitDraft;
